import { calculateTaskTotal } from './taskSystem.js';
import { CONFIG } from '../config/constants.js';
import { TASK_DEFINITIONS } from '../config/tasks.js';

/**
 * Calculates resource gains from all task rolls
 * @param {Object} params - Parameters object
 * @param {Object} params.rolls - Dice roll results by task
 * @param {Object} params.pushes - Push results by task
 * @param {Array} params.buildings - Completed buildings
 * @param {Object|null} params.event - Current event
 * @param {string|null} params.bountyTask - Task receiving bounty bonus
 * @returns {Object} Resource gains { food, wood, stone, metal, knowledge, techPoints, buildProgress }
 */
export function calculateResourceGains({ rolls, pushes = {}, buildings = [], event = null, bountyTask = null, day = null, tempBonuses = null }) {
  const gains = {
    food: 0,
    wood: 0,
    stone: 0,
    metal: 0,
    knowledge: 0,
    techPoints: 0,
    buildProgress: 0
  };

  Object.entries(rolls).forEach(([task, rollData]) => {
    const total = calculateTaskTotal({
      task,
      rollData,
      pushes,
      buildings,
      event,
      bountyTask,
      day,
      tempBonuses
    });

    // Use task definitions for dynamic resource mapping
    const taskDef = TASK_DEFINITIONS[task];
    if (!taskDef || !taskDef.resourceOutput) return;

    const output = taskDef.resourceOutput;
    const divisor = taskDef.outputDivisor || 1;

    // Map resources based on task definition
    if (output === 'food') gains.food = total;
    else if (output === 'wood') gains.wood = total;
    else if (output === 'stone') gains.stone = total;
    else if (output === 'buildProgress') gains.buildProgress = total;
    else if (output === 'techPoints') gains.techPoints = Math.floor(total / divisor);
    else if (output === 'metal') gains.metal = Math.floor(total / divisor);
    else if (output === 'knowledge') gains.knowledge = Math.floor(total / divisor);
    // Trade and rest handled separately (special mechanics)
  });

  return gains;
}

/**
 * Consumes food for workers
 * @param {number} currentFood - Current food amount
 * @param {number} aliveWorkerCount - Number of alive workers
 * @param {number} foodPerWorker - Food consumed per worker (default: 1)
 * @returns {Object} { newFood, starvationCount }
 */
export function consumeFood(currentFood, aliveWorkerCount, foodPerWorker = CONFIG.FOOD_PER_WORKER) {
  const foodNeeded = aliveWorkerCount * foodPerWorker;
  const newFood = currentFood - foodNeeded;
  const starvationCount = newFood < 0 ? Math.abs(newFood) : 0;

  return {
    newFood: Math.max(0, newFood),
    starvationCount
  };
}
